// Level-of-Care Curricula — content extracted from Catalyst_Continuum_Playbook.pdf
// Structured so each LOC page renders the same shape.

export const CURRICULA = [
  {
    id: "recovery-housing",
    segment: "Recovery Housing",
    levels: "NARR I – IV",
    los: "3 – 18 months",
    tagline: "The house is the primary vehicle for social-identity change.",
    mission:
      "Provide environmental stability so social-identity change (SIMOR), recovery capital, and lived recovery routines can take root. The milieu, the peer accountability, and the physical environment are all mobilized toward the same end.",
    documents: [
      {
        slug: "curriculum-outline",
        title: "Curriculum Outline",
        detail: "12-week rolling-arc curriculum. CHIME mapping, framework anchors, weekly modules, exercises, assessment schedule.",
        file: "/downloads/Recovery_Housing_Curriculum_Outline.pdf",
      },
      {
        slug: "facilitator-guide",
        title: "Facilitator Guide",
        detail: "Operational companion for the house-meeting leader. Verbatim language, timing, and decision rules.",
        file: "/downloads/Recovery_Housing_Facilitator_Guide.pdf",
      },
      {
        slug: "participant-workbook",
        title: "Participant Workbook",
        detail: "Resident-facing 12-week workbook. Reflection prompts, worksheets, house-meeting notes.",
        file: "/downloads/Recovery_Housing_Participant_Workbook.pdf",
      },
    ],
    schedule: [
      { block: "Morning circle", detail: "Daily community check-in with intention setting (SIMOR + CHIME-Connectedness)." },
      { block: "House chores + work / school", detail: "Structured accountability during the day (4E-enacted)." },
      { block: "Peer-run recovery meeting", detail: "12-step, SMART, Recovery Dharma, or house-run alumni meeting — 3 – 5x per week." },
      { block: "Evening curriculum group", detail: "IMR module (for SMI residents) or Seeking Safety (trauma-informed) — weekly at minimum." },
      { block: "House meeting", detail: "Governance, accountability, and grievance — weekly." },
    ],
    rotation: null,
    frameworks: [
      { name: "NARR", role: "Governs level (I peer-run through IV clinically supported) and ethics." },
      { name: "SIMOR", role: "House is the vehicle — using-group to recovery-group identity happens here first." },
      { name: "ROSC", role: "Case manager coordinates outside treatment, employment, health, and community supports." },
      { name: "IMR", role: "Nine-module curriculum for residents with co-occurring SMI. Weekly group." },
      { name: "Recovery Capital", role: "ARC or BARC-10 at intake and every 30 days." },
    ],
    facilitators: [
      "Peer specialist (Level I/II): Runs house meetings and recovery meetings.",
      "House manager (Level II/III): Enforces routines; handles conflict; coordinates with clinical.",
      "Clinical liaison (Level III/IV): Runs weekly recovery-oriented curriculum; coordinates with outpatient clinicians.",
      "Program director: Owns NARR certification and cross-site fidelity.",
    ],
    fidelity: [
      "NARR certification at declared level (I–IV) verified annually.",
      "Weekly IMR group runs for residents with SMI; attendance logged.",
      "Recovery-capital measure (ARC/BARC-10) at intake and every 30 days.",
      "Warm handoff on every intake and discharge.",
    ],
    resources: [
      { name: "Patient Handbook", href: "/downloads/Catalyst_Patient_Handbook.pdf" },
      { name: "Master Playbook (Recovery Housing chapter)", href: "/downloads/Catalyst_Continuum_Playbook.pdf" },
      { name: "Curriculum Outline (PDF)", href: "/downloads/Recovery_Housing_Curriculum_Outline.pdf" },
      { name: "Facilitator Guide (PDF)", href: "/downloads/Recovery_Housing_Facilitator_Guide.pdf" },
      { name: "Participant Workbook (PDF)", href: "/downloads/Recovery_Housing_Participant_Workbook.pdf" },
    ],
  },

  {
    id: "outpatient",
    segment: "Outpatient",
    levels: "1.0 · 1.5 · 1.7",
    los: "3 – 12 months",
    tagline: "Manage symptoms and cravings while the patient lives at home and works.",
    mission:
      "Deliver enough clinical contact to stabilize symptoms, extend recovery skills, and maintain engagement while the patient continues at home, work, or school. Level 1.7 adds physician-directed care for MOUD, chronic pain with SUD, and complex co-occurring cases.",
    documents: [
      {
        slug: "op-curriculum-outline",
        title: "Curriculum Outline",
        detail: "Weekly modules, MBC + FIT cadence, session structure, and outpatient-specific framework anchors.",
        file: "/downloads/Outpatient_Curriculum_Outline.pdf",
      },
      {
        slug: "op-facilitator-guide",
        title: "Facilitator Guide",
        detail: "Session-by-session companion for outpatient clinicians. Language, timing, and decision rules.",
        file: "/downloads/Outpatient_Facilitator_Guide.pdf",
      },
      {
        slug: "op-participant-workbook",
        title: "Participant Workbook",
        detail: "Patient-facing outpatient workbook. Reflection prompts, worksheets, between-session practice.",
        file: "/downloads/Outpatient_Participant_Workbook.pdf",
      },
    ],
    schedule: [
      { block: "1.0 — Weekly individual therapy", detail: "Standard OP. 1 individual session per week; 1 group every other week; case management as needed." },
      { block: "1.5 — Intensive Outpatient Services", detail: "6–8 hours / week. 2–3 group sessions × 2h + 1 individual + monthly family + case management." },
      { block: "1.7 — Medically Managed Outpatient", detail: "1.5 rhythm plus physician / PMHNP visits monthly to quarterly, RN or LPN case-managing medication complexity." },
      { block: "Curriculum group", detail: "Rotating: Seeking Safety (trauma-informed), IMR (co-occurring SMI), and CBT/DBT skills groups." },
    ],
    rotation: null,
    frameworks: [
      { name: "ASAM", role: "Weekly LOC review; use the six dimensions to decide step-up or step-down." },
      { name: "MBC", role: "PHQ-9 + GAD-7 every visit; full battery at intake, monthly, and at LOC transitions." },
      { name: "FIT", role: "ORS at start, SRS at end of every individual and group session." },
      { name: "Seeking Safety", role: "Weekly group for the trauma-affected subpopulation (screen with PCL-5)." },
      { name: "IMR", role: "Weekly group for co-occurring SMI residents; nine modules across LOS." },
    ],
    facilitators: [
      "Therapist: Runs individual therapy; screens; coordinates trauma-processing referrals.",
      "Peer specialist / BHT: Co-facilitates groups; carries language into the milieu.",
      "Case manager: Coordinates community supports, employment, housing (ROSC).",
      "Prescriber (1.7): Physician / PMHNP / PA sees at defined intervals; RN or LPN case-manages complexity.",
    ],
    fidelity: [
      "ORS + SRS every session (100% chart audit).",
      "PHQ-9 + GAD-7 trend visible at every clinical touch.",
      "Two consecutive weeks above threshold triggers case-conference for step-up.",
      "Seeking Safety group runs weekly at fidelity; attendance and curriculum sequence logged.",
    ],
    resources: [
      { name: "Master Playbook (Outpatient chapter)", href: "/downloads/Catalyst_Continuum_Playbook.pdf" },
      { name: "Framework Addendum 2026", href: "/downloads/Catalyst_Framework_Addendum_2026.pdf" },
      { name: "Curriculum Outline (PDF)", href: "/downloads/Outpatient_Curriculum_Outline.pdf" },
      { name: "Facilitator Guide (PDF)", href: "/downloads/Outpatient_Facilitator_Guide.pdf" },
      { name: "Participant Workbook (PDF)", href: "/downloads/Outpatient_Participant_Workbook.pdf" },
    ],
  },

  {
    id: "iop-php",
    segment: "IOP / PHP",
    levels: "2.1 · 2.5 · 2.7",
    los: "4 – 12 weeks",
    tagline: "Enough structure to interrupt the addictive pattern while the patient remains in their home.",
    mission:
      "Bridge from residential to independent recovery, or step up from outpatient when 1.5 was not enough. Approaches residential in service hours; distinguished by the patient returning home each night.",
    documents: [
      {
        slug: "iop-curriculum-outline",
        title: "Curriculum Outline",
        detail: "12-week IOP/PHP rotation with daily rhythm, weekly modules, and CHIME-tagged group agendas.",
        file: "/downloads/IOP_PHP_Curriculum_Outline.pdf",
      },
      {
        slug: "iop-facilitator-guide",
        title: "Facilitator Guide",
        detail: "Session-by-session guide for therapists and BHTs. Language, timing, and treatment-team hand-offs.",
        file: "/downloads/IOP_PHP_Facilitator_Guide.pdf",
      },
      {
        slug: "iop-participant-workbook",
        title: "Participant Workbook",
        detail: "Patient-facing workbook aligned to the 12-week IOP/PHP curriculum.",
        file: "/downloads/IOP_PHP_Participant_Workbook.pdf",
      },
    ],
    schedule: [
      { block: "5:30–5:45", detail: "MBC + ORS" },
      { block: "5:45–6:00", detail: "Check-in circle (SIMOR, CHIME-Connectedness)" },
      { block: "6:00–7:15", detail: "Process or psychoeducation group (curriculum rotates)" },
      { block: "7:15–7:30", detail: "Break" },
      { block: "7:30–8:15", detail: "Skills group (CBT / DBT / relapse prevention)" },
      { block: "8:15–8:25", detail: "Individual check-in with therapist (rotating) or peer specialist" },
      { block: "8:25–8:30", detail: "Closing circle + SRS" },
    ],
    rotation: [
      { week: 1, topic: "Recovery identity", detail: "SIMOR intro — who am I becoming?" },
      { week: 2, topic: "The four Es of my recovery", detail: "Body / environment / action / tools (4E)." },
      { week: 3, topic: "Cravings and triggers", detail: "CBT + interoceptive awareness." },
      { week: 4, topic: "Emotion regulation", detail: "DBT skills; ACT defusion." },
      { week: 5, topic: "Relationships and boundaries", detail: "Attachment; family systems." },
      { week: 6, topic: "Grief and loss", detail: "Including grief for the using self." },
      { week: 7, topic: "Meaning and purpose", detail: "CHIME-Meaning; values work." },
      { week: 8, topic: "Empowerment and self-advocacy", detail: "CHIME-Empowerment." },
      { week: 9, topic: "Hope and story", detail: "CHIME-Hope; recovery narratives." },
      { week: 10, topic: "Community and connection", detail: "CHIME-Connectedness; SIMOR groups." },
      { week: 11, topic: "Relapse prevention plan finalization", detail: "" },
      { week: 12, topic: "Discharge and continuing care planning", detail: "" },
    ],
    frameworks: [
      { name: "ASAM", role: "Weekly LOC review; day-treatment tiers are the most volatile in movement up/down." },
      { name: "FIT", role: "Group ORS at start, group SRS at end. Individual sessions the same." },
      { name: "MBC", role: "Full battery at admission, weekly short forms, monthly full, and at discharge." },
      { name: "SIMOR", role: "Recovery-identity group is the anchor of the week." },
      { name: "4E", role: "Somatic / experiential daily in 2.5+; embodied programming baked into the schedule." },
      { name: "CHIME", role: "Every group tagged with the domain(s) it primarily targets." },
    ],
    facilitators: [
      "Therapist: Owns individual therapy; runs process group; leads curriculum sequence.",
      "BHT: Runs check-in / closing; carries curriculum language into the milieu.",
      "Case manager: Coordinates external supports (ROSC); attends weekly treatment team.",
      "Prescriber (2.7): Physician / PMHNP contact 2–3× per week; RN on-site daily.",
    ],
    fidelity: [
      "12-week curriculum runs sequentially without gaps.",
      "Family sessions or multi-family group at weeks 2, 6, and 10.",
      "Family psychoeducation series parallel to the patient curriculum.",
      "ORS + SRS at 100% of sessions (individual and group).",
      "Every treatment plan explicitly maps at least one goal per CHIME domain.",
    ],
    resources: [
      { name: "Master Playbook (IOP / PHP chapter)", href: "/downloads/Catalyst_Continuum_Playbook.pdf" },
      { name: "Reference Card — 19 Frameworks", href: "/downloads/CatalystReferenceCard_19_frameworks.pdf" },
      { name: "Curriculum Outline (PDF)", href: "/downloads/IOP_PHP_Curriculum_Outline.pdf" },
      { name: "Facilitator Guide (PDF)", href: "/downloads/IOP_PHP_Facilitator_Guide.pdf" },
      { name: "Participant Workbook (PDF)", href: "/downloads/IOP_PHP_Participant_Workbook.pdf" },
    ],
  },

  {
    id: "residential",
    segment: "Residential",
    levels: "3.1 · 3.3 · 3.5",
    los: "30 – 180 days",
    tagline: "The milieu is the intervention. Clinical hours are augmentation.",
    mission:
      "Provide 24-hour non-medical residential care with a fully saturated recovery environment. Room design, meal rhythm, food, movement, and staff behavior are all intervention. Clinical hours amplify the milieu — they do not replace it.",
    documents: [
      {
        slug: "res-curriculum-outline",
        title: "Curriculum Outline",
        detail: "Residential curriculum arc across 30–180 day LOS. Paired-week rotation with CHIME-tagged programming.",
        file: "/downloads/Residential_Curriculum_Outline.pdf",
      },
      {
        slug: "res-facilitator-guide",
        title: "Facilitator Guide",
        detail: "24-hour milieu operational guide. BHT + therapist + case manager language, timing, and decision rules.",
        file: "/downloads/Residential_Facilitator_Guide.pdf",
      },
      {
        slug: "res-participant-workbook",
        title: "Participant Workbook",
        detail: "Resident-facing workbook aligned to the paired-week residential curriculum.",
        file: "/downloads/Residential_Participant_Workbook.pdf",
      },
    ],
    schedule: [
      { block: "07:00", detail: "Wake, hygiene, community breakfast." },
      { block: "08:00", detail: "Morning community meeting — SIMOR + CHIME-Connectedness." },
      { block: "09:00–12:00", detail: "Group programming block: process, skills, curriculum (CHIME-tagged)." },
      { block: "12:00", detail: "Lunch — milieu is programming, not a gap." },
      { block: "13:00–15:00", detail: "Individual therapy / case management / medical rotations." },
      { block: "15:00–16:00", detail: "Somatic / experiential — yoga, art, movement (4E embodied)." },
      { block: "16:00–17:30", detail: "Psychoeducation or curriculum rotation group." },
      { block: "18:00", detail: "Community dinner and reflection." },
      { block: "19:00–20:30", detail: "Evening group — peer-run meeting or Seeking Safety / IMR (SMI subpopulation)." },
      { block: "21:00", detail: "Community closing; personal reflection; SRS." },
    ],
    rotation: [
      { week: "1–2", topic: "Recovery identity + stabilization", detail: "SIMOR intro; medication stabilization; safety planning." },
      { week: "3–4", topic: "Cravings, triggers, and body", detail: "4E-embodied; interoceptive awareness; craving surfing." },
      { week: "5–6", topic: "Grief and family systems", detail: "Grief for the using-self; family session cycle begins." },
      { week: "7–8", topic: "Meaning and purpose", detail: "Values clarification; contribution planning." },
      { week: "9–10", topic: "Hope and story", detail: "Recovery narratives; identity in the recovery-group (SIMOR)." },
      { week: "11–12", topic: "Aftercare and transition", detail: "Discharge planning; warm handoff to 2.5/2.1/1.7." },
    ],
    frameworks: [
      { name: "SIMOR", role: "The house / unit is the primary vehicle for social-identity change." },
      { name: "4E", role: "Community, rhythm, food, movement, staff behavior — all are interventions." },
      { name: "CHIME", role: "Curriculum tagged; treatment plans tagged; discharge readiness assessed against CHIME." },
      { name: "FIT", role: "ORS morning, SRS evening; group ORS/SRS every session." },
      { name: "MBC", role: "PHQ-9, GAD-7 weekly; full battery every 30 days and at discharge." },
      { name: "Seeking Safety + IMR", role: "Weekly groups; IMR nine modules across LOS for SMI subpopulation." },
    ],
    facilitators: [
      "BHT: Owns the milieu — 80% of the healing minutes happen here.",
      "Therapist: Runs process group and 2× weekly individual; owns FIT + MBC.",
      "Case manager: Owns ASAM Dimension 5–6 work; coordinates aftercare.",
      "Nursing: Medical rounds; medication management; withdrawal monitoring where 3.5 handles it.",
      "Program director: Ensures curriculum runs at fidelity; owns fidelity audits.",
    ],
    fidelity: [
      "Every treatment plan maps at least one goal per CHIME domain (100% chart audit).",
      "CHIME audit of the curriculum monthly — any under-represented domain gets curriculum added.",
      "Weekly Seeking Safety group runs at fidelity (attendance + curriculum sequence).",
      "IMR groups run for SMI residents 3–5× weekly at 3.1–3.5.",
      "3.3 adaptations for cognitive-impairment population: slower pace, concrete visual curriculum, higher staffing ratios.",
    ],
    resources: [
      { name: "Master Playbook (Residential chapter)", href: "/downloads/Catalyst_Continuum_Playbook.pdf" },
      { name: "Clinical Onboarding Playbook", href: "/downloads/Catalyst_Clinical_Onboarding_Playbook.pdf" },
      { name: "Curriculum Outline (PDF)", href: "/downloads/Residential_Curriculum_Outline.pdf" },
      { name: "Facilitator Guide (PDF)", href: "/downloads/Residential_Facilitator_Guide.pdf" },
      { name: "Participant Workbook (PDF)", href: "/downloads/Residential_Participant_Workbook.pdf" },
    ],
  },

  {
    id: "medical",
    segment: "Medically Monitored & Managed",
    levels: "3.7 · 4.0",
    los: "3 – 21 days",
    tagline: "Nurse-staffed and physician-directed. Withdrawal made a therapeutic event.",
    mission:
      "Deliver 24-hour nursing care and daily physician oversight for patients whose biomedical, psychiatric, or withdrawal acuity exceeds what 3.5 can safely manage. Level 3.7 is nurse-staffed / physician-directed; Level 4.0 is hospital-level medically managed inpatient.",
    documents: [
      {
        slug: "med-curriculum-outline",
        title: "Curriculum Outline",
        detail: "Recovery-oriented programming calibrated to withdrawal / medical acuity at Levels 3.7 and 4.0.",
        file: "/downloads/Medical_Curriculum_Outline.pdf",
      },
      {
        slug: "med-facilitator-guide",
        title: "Facilitator Guide",
        detail: "Nursing + BHT + medical language and timing. Warm-handoff protocols and CIWA/COWS integration.",
        file: "/downloads/Medical_Facilitator_Guide.pdf",
      },
      {
        slug: "med-participant-workbook",
        title: "Participant Workbook",
        detail: "Patient-facing workbook for medically monitored and managed stays. Short-form, CHIME-Hope focused.",
        file: "/downloads/Medical_Participant_Workbook.pdf",
      },
    ],
    schedule: [
      { block: "Continuous", detail: "24/7 RN coverage. CIWA (alcohol) / COWS (opioids) / benzo protocols assessed at scheduled intervals." },
      { block: "Daily", detail: "Physician / PMHNP rounding; medication adjustment; medical stabilization." },
      { block: "Daily", detail: "Short recovery-oriented milieu blocks when patient is medically able — CHIME-Hope focus." },
      { block: "Daily", detail: "Case manager engages patient once medically stable — begins Dim 5–6 work and aftercare planning." },
      { block: "As able", detail: "IMR module 1 (Recovery Strategies) can be introduced for SMI step-down populations." },
    ],
    rotation: null,
    frameworks: [
      { name: "ASAM Dim 1–3", role: "Withdrawal severity, biomedical, psychiatric acuity govern length of stay and step-down." },
      { name: "MBC (medical)", role: "CIWA / COWS every 2–4 hours; PHQ-9 / GAD-7 once medically stable." },
      { name: "4E — embodied", role: "Somatic awareness during withdrawal — grounding, interoception, orientation." },
      { name: "CHIME — Hope", role: "The primary CHIME focus in this LOC: 'You are going to feel human again.'" },
      { name: "ROSC", role: "Warm handoff scheduled before medical discharge; receiving clinician confirmed." },
    ],
    facilitators: [
      "Nursing: 1:8–1:12 ratio at 3.7; hospital-level at 4.0. RN owns CIWA/COWS scoring.",
      "Medical (MD / DO / PMHNP / PA): Physician-directed treatment plan; MAT initiation as indicated.",
      "BHT: Presence and orientation during withdrawal — carries Hope into the room.",
      "Case manager: Begins Dim 5–6 work as soon as medically stable; owns warm handoff.",
    ],
    fidelity: [
      "24/7 nursing coverage confirmed on every shift.",
      "CIWA/COWS scoring completed at the frequency the protocol specifies (usually every 2–4 hours in acute withdrawal).",
      "MAT — buprenorphine, methadone, naltrexone — offered per current standards.",
      "Warm handoff to 3.5 / 2.5 / 1.7 completed BEFORE discharge; receiving clinician confirmed by name.",
      "No discharge without a next appointment on the receiving clinician's calendar.",
    ],
    resources: [
      { name: "Master Playbook (Medically Monitored & Managed chapter)", href: "/downloads/Catalyst_Continuum_Playbook.pdf" },
      { name: "Curriculum Outline (PDF)", href: "/downloads/Medical_Curriculum_Outline.pdf" },
      { name: "Facilitator Guide (PDF)", href: "/downloads/Medical_Facilitator_Guide.pdf" },
      { name: "Participant Workbook (PDF)", href: "/downloads/Medical_Participant_Workbook.pdf" },
    ],
  },
];

export function findCurriculum(id) {
  return CURRICULA.find((c) => c.id === id);
}
